import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, Calendar, Clock, Mail, Info, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

/**
 * BookingConfirmation Component
 * 
 * Displays confirmation after customer successfully books a consultation.
 * Shows scheduled time and next steps (Zoom link coming separately).
 */
export function BookingConfirmation({
  bookingData,
  customerName,
  customerEmail,
  onClose
}) {
  const {
    scheduled_date,
    scheduled_time,
    consultation_id,
    customer_token
  } = bookingData || {};

  // Format time for display (09:00 -> 9:00 AM ET)
  const formatTimeSlot = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period} ET`;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return format(date, 'EEEE, MMMM d, yyyy');
  };

  // Build Add to Calendar URL (Google Calendar)
  const getGoogleCalendarUrl = () => {
    if (!scheduled_date || !scheduled_time) return '#';
    
    const [hours, minutes] = scheduled_time.split(':').map(Number);
    const startDate = new Date(scheduled_date + 'T00:00:00');
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + 30);
    
    const formatForGoogle = (date) => {
      return date.toISOString().replace(/-|:|\.\d{3}/g, '');
    };
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: 'KGOB CPAs Consultation',
      dates: `${formatForGoogle(startDate)}/${formatForGoogle(endDate)}`,
      details: 'Your consultation with KGOB CPAs & Advisors. Zoom link will be sent separately via email.',
      location: 'Zoom (link to be sent)',
      sf: 'true'
    });
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="booking-confirmation">
      {/* Success Header */}
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle2 className="h-10 w-10 text-green-600" />
        </div>
        <h1 className="text-3xl font-bold text-[#0B2240] mb-2">
          Consultation Confirmed!
        </h1>
        <p className="text-[#4C5A70]">
          Thank you{customerName ? `, ${customerName}` : ''}! Your consultation has been scheduled.
        </p>
      </div>

      {/* Booking Details Card */}
      <Card className="shadow-lg border-t-4 border-t-[#1FA3A3]">
        <CardHeader>
          <CardTitle className="text-[#0B2240]">Your Booking Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            <Calendar className="h-6 w-6 text-[#1FA3A3] mt-0.5" />
            <div>
              <p className="font-medium text-[#0B2240]">{formatDate(scheduled_date)}</p>
              <p className="text-[#4C5A70]">{formatTimeSlot(scheduled_time)}</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            <Mail className="h-6 w-6 text-[#1FA3A3] mt-0.5" />
            <div>
              <p className="font-medium text-[#0B2240]">Confirmation Email Sent</p>
              <p className="text-[#4C5A70]">{customerEmail}</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            <Clock className="h-6 w-6 text-[#1FA3A3] mt-0.5" />
            <div>
              <p className="font-medium text-[#0B2240]">Duration</p>
              <p className="text-[#4C5A70]">30 minutes</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zoom Link Notice */}
      <Alert className="bg-amber-50 border-amber-200">
        <Info className="h-5 w-5 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong className="block mb-1">Zoom Link Coming Soon</strong>
          You'll receive a separate email with your Zoom meeting link before your scheduled consultation.
        </AlertDescription>
      </Alert>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          variant="outline"
          className="border-[#1FA3A3] text-[#1FA3A3] hover:bg-[#1FA3A3]/10"
          onClick={() => window.open(getGoogleCalendarUrl(), '_blank')}
          data-testid="add-to-calendar-btn"
        >
          <Calendar className="h-4 w-4 mr-2" />
          Add to Google Calendar
        </Button>
        
        {customer_token && (
          <Button
            variant="outline"
            className="border-[#0B2240] text-[#0B2240] hover:bg-[#0B2240]/10"
            onClick={() => window.open(`/book-consultation/view/${customer_token}`, '_blank')}
            data-testid="view-booking-btn"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            View Booking Details
          </Button>
        )}
      </div>

      {/* Next Steps */}
      <Card className="bg-[#0B2240] text-white">
        <CardContent className="p-6">
          <h3 className="font-bold text-lg mb-4 text-[#D4AF37]">What's Next?</h3>
          <ol className="space-y-3 text-gray-200">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1FA3A3] text-white text-sm flex items-center justify-center font-bold">1</span>
              <span>Check your email for the booking confirmation</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1FA3A3] text-white text-sm flex items-center justify-center font-bold">2</span>
              <span>Look out for a second email with your Zoom meeting link</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1FA3A3] text-white text-sm flex items-center justify-center font-bold">3</span>
              <span>Join the Zoom call 5 minutes early to test your setup</span>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Return Home */}
      {onClose && (
        <div className="text-center">
          <Button data-testid="booking-confirmation-button-1"
            variant="ghost"
            className="text-[#4C5A70] hover:text-[#0B2240]"
            onClick={onClose}
          >
            Return to Homepage
          </Button>
        </div>
      )}
    </div>
  );
}

export default BookingConfirmation;
